import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Card } from '@clari/ui'
import { bankingApi } from '@/lib/banking-api'

type Phase = 'loading' | 'success' | 'error'

/**
 * Swan OAuth redirect target (registered Dashboard URL: /onboarding/callback).
 * Reads { code, state } and finalizes the onboarding via the API, then sends the
 * user to their dashboard where the account shows up in "opening" state.
 */
export function OnboardingCallback() {
  const { t } = useTranslation('banking')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [phase, setPhase] = useState<Phase>('loading')
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return // guard against React strict-mode double-invoke
    ran.current = true
    const code = params.get('code')
    const state = params.get('state')
    if (!code || !state) {
      setPhase('error')
      return
    }
    bankingApi
      .completeOnboarding({ code, state })
      .then(() => setPhase('success'))
      .catch(() => setPhase('error'))
  }, [params])

  return (
    <div className="mx-auto mt-16 max-w-xl px-4">
      <Card>
        {phase === 'loading' ? (
          <>
            <h1 className="text-2xl font-semibold">{t('onboarding.finalizing_title')}</h1>
            <p className="mt-2 text-text-muted">{t('onboarding.finalizing_body')}</p>
          </>
        ) : null}
        {phase === 'success' ? (
          <>
            <h1 className="text-2xl font-semibold">{t('onboarding.success_title')}</h1>
            <p className="mt-2 text-text-muted">{t('onboarding.success_body')}</p>
            <Button className="mt-6" onClick={() => navigate('/app', { replace: true })} withSignatureDot>
              {t('onboarding.success_cta')}
            </Button>
          </>
        ) : null}
        {phase === 'error' ? (
          <>
            <h1 className="text-2xl font-semibold">{t('onboarding.error_title')}</h1>
            <p className="mt-2 text-text-muted">{t('onboarding.error_body')}</p>
            <Button className="mt-6" variant="secondary" onClick={() => navigate('/app', { replace: true })}>
              {t('onboarding.error_cta')}
            </Button>
          </>
        ) : null}
      </Card>
    </div>
  )
}
